import { useEffect } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, FileIcon, Play } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { BugReport, BugReportAttachment } from "@/hooks/use-bug-reports";
import { isImageType, isVideoType } from "@/lib/bug-attachments";

type AttachmentOwner = Pick<BugReport, "id" | "title" | "attachments">;

/** Ruta vraća 302 na presigned R2 URL pa se koristi direktno kao src/href. */
export function attachmentUrl(reportId: string, a: BugReportAttachment): string {
  return `/api/bug-reports/${reportId}/attachments/${a.id}`;
}

export function AttachmentStrip({
  report,
  onOpen,
}: {
  report: AttachmentOwner;
  onOpen: (index: number) => void;
}) {
  const attachments = report.attachments ?? [];
  if (attachments.length === 0) return null;

  return (
    <ul className="flex flex-wrap gap-2">
      {attachments.map((a, i) => {
        const image = isImageType(a.contentType);
        const video = isVideoType(a.contentType);
        if (!image && !video) {
          return (
            <li key={a.id}>
              <a
                href={attachmentUrl(report.id, a)}
                target="_blank"
                rel="noreferrer"
                className="flex h-16 max-w-56 items-center gap-2 border px-3 outline-none transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <FileIcon className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{a.fileName}</span>
              </a>
            </li>
          );
        }
        return (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => onOpen(i)}
              aria-label={`Otvori ${a.fileName}`}
              title={a.fileName}
              className="relative flex size-16 items-center justify-center overflow-hidden border bg-muted outline-none transition-colors hover:border-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              {image ? (
                <img
                  src={attachmentUrl(report.id, a)}
                  alt=""
                  loading="lazy"
                  className="size-full object-cover"
                />
              ) : (
                <>
                  <Play className="size-5 text-muted-foreground" />
                  <span className="absolute bottom-1 left-1 bg-foreground px-1 text-[10px] font-medium uppercase text-background">
                    Video
                  </span>
                </>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Pregled slike/videa preko cijelog ekrana, sa listanjem kroz priloge iste prijave. */
export function AttachmentViewer({
  report,
  index,
  onIndexChange,
  onClose,
}: {
  report: AttachmentOwner;
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  // Fajlovi koji nisu slika ni video otvaraju se direktno, pa ih pregled preskače.
  const viewable = report.attachments
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => isImageType(a.contentType) || isVideoType(a.contentType));
  const position = viewable.findIndex(({ i }) => i === index);
  const current = viewable[position];

  function step(delta: number) {
    const next = viewable[(position + delta + viewable.length) % viewable.length];
    if (next) onIndexChange(next.i);
  }

  useEffect(() => {
    if (viewable.length < 2) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!current) return null;
  const url = attachmentUrl(report.id, current.a);
  const arrowClass =
    "absolute top-1/2 flex size-9 -translate-y-1/2 items-center justify-center border bg-background outline-none transition-colors hover:bg-foreground hover:text-background focus-visible:ring-[3px] focus-visible:ring-ring/50";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="gap-0 p-0 sm:max-w-4xl">
        <div className="flex items-center gap-3 border-b py-3 pl-4 pr-12">
          <div className="min-w-0 flex-1">
            <DialogTitle className="truncate text-sm font-medium">{current.a.fileName}</DialogTitle>
            <DialogDescription className="truncate text-[11px]">{report.title}</DialogDescription>
          </div>
          {viewable.length > 1 && (
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {position + 1} / {viewable.length}
            </span>
          )}
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            aria-label="Otvori original u novom tabu"
            title="Otvori original u novom tabu"
            className="shrink-0 opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <ExternalLink className="size-4" />
          </a>
        </div>
        <div className="relative flex min-h-48 items-center justify-center bg-muted">
          {isImageType(current.a.contentType) ? (
            <img
              key={current.a.id}
              src={url}
              alt={current.a.fileName}
              className="max-h-[78vh] w-auto max-w-full object-contain"
            />
          ) : (
            <video
              key={current.a.id}
              src={url}
              controls
              autoPlay
              className="max-h-[78vh] w-full bg-black"
            />
          )}
          {viewable.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label="Prethodni prilog"
                className={cn(arrowClass, "left-2")}
              >
                <ChevronLeft className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                aria-label="Sljedeći prilog"
                className={cn(arrowClass, "right-2")}
              >
                <ChevronRight className="size-4" />
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
